import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { DEFAULT_SELECTED_STATE, getStateDetails } from '../../data/overviewData';

const GEO_URL = '/nigeria-states.json';

// Zone colour palette — matches the Figma map image exactly
const ZONE_COLORS = {
  'North Central': '#c89b3e',
  'North East':    '#6f7d32',
  'North West':    '#1e6a43',
  'South East':    '#2e8271',
  'South West':    '#b25b3d',
  'South South':   '#38617c',
};

const ZONES = [
  { label: 'North - Central', key: 'North Central', color: '#c89b3e' },
  { label: 'North - East',    key: 'North East',    color: '#6f7d32' },
  { label: 'North - West',    key: 'North West',    color: '#1e6a43' },
  { label: 'South - East',    key: 'South East',    color: '#2e8271' },
  { label: 'South - West',    key: 'South West',    color: '#b25b3d' },
  { label: 'South - South',   key: 'South South',   color: '#38617c' },
];

// State borders — thin dark line on the light map card (Figma), white when hovered/selected
const BORDER_COLOR = '#333333';
const BORDER_WIDTH = 0.6;
const ACTIVE_BORDER_COLOR = '#ffffff';
const ACTIVE_BORDER_WIDTH = 2.5;

function formatCompact(v) {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} M`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(0)} K`;
  return String(v);
}

const STAT_ROWS = [
  { key: 'livestockPopulation',     label: 'Livestock Population',           format: formatCompact },
  { key: 'livestockMarkets',        label: 'Livestock Markets' },
  { key: 'registeredFacilities',    label: 'Registered Livestock Facilities' },
  { key: 'registeredVeterinarians', label: 'Registered Veterinarians' },
  { key: 'coordinator',             label: 'State Coordinator' },
  { key: 'coordinatorPhone',        label: 'State Coordinator No.' },
];

export default function NigeriaLivestockOverview() {
  const [selected, setSelected] = useState(DEFAULT_SELECTED_STATE);
  const [tooltip,  setTooltip]  = useState(null);

  // getStateDetails uses the human-readable name (e.g. "Kaduna")
  const details = useMemo(() => getStateDetails(selected), [selected]);

  return (
    <section className="bg-white py-8 text-[#0f3a26] lg:py-[70px]">
      <div className="mx-auto flex max-w-[1440px] flex-col gap-6 px-4 sm:px-8 lg:px-10 pub-xl:px-[100px]">

        <div className="flex flex-col gap-3">
          <h2 className="w-full font-quattrocento text-xl font-bold text-[#0f3a26] sm:text-2xl lg:w-[817px] lg:text-[32px]">
            Nigeria&apos;s livestock Information at a{' '}
            <span className="text-[#e7c77e]">glance</span>.
          </h2>
          <p className="text-sm text-[#0f3a26] sm:text-base lg:text-[18px]">
            Tap or hover around a state shaded by geopolitical zone to see published livestock
            population. Select a state to preview its statistics.
          </p>
        </div>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">

          {/* ── Left: interactive map ─────────────────────────── */}
          <div
            className="relative flex w-full flex-col gap-6 overflow-hidden rounded-2xl bg-[rgba(217,251,212,0.5)] p-4 sm:p-6 lg:flex-1 lg:gap-4 lg:p-5"
            style={{ border: '0.55px solid #ffffff' }}
          >
            {tooltip && (
              <div className="pointer-events-none absolute left-1/2 top-3 z-10 -translate-x-1/2 whitespace-nowrap rounded-md bg-black/70 px-3 py-1.5 text-xs font-semibold text-white">
                {tooltip}
              </div>
            )}

            <ComposableMap
              projection="geoMercator"
              projectionConfig={{ center: [8.68, 8.9], scale: 3600 }}
              width={784}
              height={635}
              style={{ width: '100%', height: 'auto', maxHeight: 'calc(64vh * 0.9 / var(--public-zoom, 0.9))', display: 'block', margin: '0 auto' }}
            >
              <Geographies geography={GEO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    // GADM uses NAME_1; display_name is the human-readable label we added
                    const displayName = geo.properties.display_name ?? geo.properties.NAME_1;
                    const zone        = geo.properties.zone ?? 'North Central';
                    const fill        = ZONE_COLORS[zone] ?? '#c89b3e';
                    const isActive    = displayName === selected;

                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        /* Pass fill + stroke as direct SVG props so v5 applies them
                           regardless of the style-object approach */
                        fill={fill}
                        stroke={isActive ? ACTIVE_BORDER_COLOR : BORDER_COLOR}
                        strokeWidth={isActive ? ACTIVE_BORDER_WIDTH : BORDER_WIDTH}
                        onClick={() => setSelected(displayName)}
                        onMouseEnter={(e) => {
                          e.target.style.stroke = ACTIVE_BORDER_COLOR;
                          e.target.style.strokeWidth = '1.8';
                          e.target.style.opacity = '1';
                          e.target.style.cursor = 'pointer';
                          setTooltip(displayName);
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.stroke = isActive ? ACTIVE_BORDER_COLOR : BORDER_COLOR;
                          e.target.style.strokeWidth = String(isActive ? ACTIVE_BORDER_WIDTH : BORDER_WIDTH);
                          e.target.style.opacity = isActive ? '1' : '0.92';
                          setTooltip(null);
                        }}
                        style={{ outline: 'none', opacity: isActive ? 1 : 0.92 }}
                      />
                    );
                  })
                }
              </Geographies>
            </ComposableMap>

            {/* Zone legend */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              {ZONES.map((zone) => (
                <div
                  key={zone.key}
                  className="flex items-center gap-2 rounded-[20px] bg-[#f1f4f9] p-2"
                  style={{ border: '0.22px solid #ffffff' }}
                >
                  <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: zone.color }} />
                  <span className="whitespace-nowrap text-[12px] font-semibold" style={{ color: zone.color }}>{zone.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right: state info panel ───────────────────────── */}
          <div
            className="flex w-full flex-col gap-2 rounded-2xl bg-[#ebfce9] px-4 py-4 lg:w-[290px] lg:shrink-0"
            style={{ border: '0.55px solid #ffffff' }}
          >
            <p className="text-[14px] font-semibold text-[#e7c77e]">SELECTED STATE</p>

            <div className="py-1" style={{ borderBottom: '0.22px solid #333333' }}>
              <p className="font-quattrocento text-lg font-bold text-black">{details.name}</p>
            </div>

            {STAT_ROWS.map((row) => (
              <div
                key={row.key}
                className="flex items-center justify-between gap-3 py-1 text-[13px] text-black"
                style={{ borderBottom: '0.22px solid #333333' }}
              >
                <span>{row.label}</span>
                <span className="text-right">
                  {row.format ? row.format(details[row.key]) : details[row.key]}
                </span>
              </div>
            ))}

            <Link
              to={`state-overview?state=${encodeURIComponent(selected)}`}
              state={{ state: selected }}
              className="flex w-full items-center justify-center gap-5 rounded-lg bg-[#1fc16b] px-[10px] py-1.5 text-[14px] text-white transition hover:bg-[#19a95c]"
            >
              View more
              <FiArrowRight className="h-4 w-4" />
            </Link>
          </div>

        </div>
      </div>
    </section>
  );
}
